import { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { styled } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import UsersMenu from '@features/UsersMenu';
import Button from '@components/Button';
import { Typography } from '@components/Typography';
import Avatar from '@components/Avatar';
import { UserIcon, PulseAiIcon } from 'assets/images/icons';
import { getFullName, getInitialsParts, getUserContacts } from '@helpers/userName';
import { handleApiError } from '@helpers/api';
import { useTranslation, useUsersOptions, useConversations } from '@hooks';
import { ConversationStatus } from '@constants/conversations';
import { CHANNEL } from '@constants/channels';
import { contactsActions, alertActions, inboxActions } from '@actions';
import { conversationsService } from '@services';

const StyledUserIcon = styled(UserIcon, {
    shouldForwardProp: prop => prop !== 'isLarge',
})(({ isLarge }) => ({
    width: `${isLarge ? 18 : 16}px !important`,
    height: `${isLarge ? 18 : 16}px !important`,
    '& path': {
        fill: 'rgba(0, 0, 0, 0.38)',
        fillOpacity: 1,
    },
}));

const StyledAvatar = styled(Avatar)({
    border: 'none',
});

const InboxHeader = ({
    isMobile,
    activeConversation,
    setActiveConversation,
    locations,
    filterRef,
    setContactOpened,
}) => {
    const [isSolvingConversation, setIsSolvingConversation] = useState(false);

    const { contact } = useSelector(state => state.contacts);
    const { data: messages } = useSelector(state => state.inbox);
    const currentUser = useSelector(state => state.auth.user);

    const { t } = useTranslation();
    const isLargeScreenWithOpenedContact = useMediaQuery('@media (min-width: 1025px) and (max-width: 1100px)');
    const dispatch = useDispatch();
    const { getUsersOptionsByLocationIds } = useUsersOptions();
    const { updateConversationInFeedAndReturn } = useConversations();

    const conversationCompanies = useMemo(
        () => locations.filter(l => l.channels.some(c => c === activeConversation?.channelId)).map(l => l.companyId),
        [locations, activeConversation],
    );

    const usersOptions = useMemo(() => getUsersOptionsByLocationIds(conversationCompanies), [conversationCompanies]);

    const onClickBack = () => setActiveConversation(null);

    const onClickContactDetails = () => {
        if (activeConversation?.contactId !== contact?.id) {
            dispatch(contactsActions.getContact(activeConversation?.contactId));
        }
        setContactOpened(true);
    };

    const onResolveConversation = async () => {
        try {
            setIsSolvingConversation(true);
            const apiCall = isOpen ? conversationsService.close : conversationsService.open;
            const { message, conversation } = await apiCall(activeConversation?.id);
            setActiveConversation(conversation);
            dispatch(inboxActions.update([...messages, message]));
            updateConversationInFeedAndReturn(conversation, filterRef.current, true);
        } catch (error) {
            handleApiError(error);
        } finally {
            setIsSolvingConversation(false);
        }
    };

    const onAssignUser = async userId => {
        if (activeConversation?.assignedUserId === userId) {
            return;
        }
        try {
            const conversation = await conversationsService.assign(activeConversation?.id, {
                userId: userId || currentUser?.id,
            });
            setActiveConversation(conversation);
            updateConversationInFeedAndReturn(conversation, filterRef.current, true);
            dispatch(alertActions.success(t('alertMessages.updateSuccess')));
        } catch (error) {
            handleApiError(error);
        }
    };

    const isOpen = activeConversation?.status === ConversationStatus.Open;
    const isArchived = activeConversation?.channelId === CHANNEL.ARCHIVED;

    return (
        <>
            {isMobile && (
                <Button sx={{ minWidth: 'auto' }} variant="outlined" onClick={onClickBack}>
                    <ArrowBackIcon sx={{ fill: 'rgba(0, 0, 0, 0.6)' }} />
                </Button>
            )}
            <Avatar
                size={isMobile ? 'medium' : 'large'}
                fullName={getFullName(activeConversation?.firstName, activeConversation?.lastName)}
                label={getInitialsParts(activeConversation?.firstName, activeConversation?.lastName)}
                icon={<StyledUserIcon isLarge={!isMobile} />}
            />
            {activeConversation && (
                <>
                    <div style={{ overflow: 'hidden' }}>
                        <Typography
                            variant="subtitle1"
                            sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                        >
                            {getUserContacts(
                                {
                                    firstName: activeConversation?.firstName,
                                    lastName: activeConversation?.lastName,
                                    phoneNumber: activeConversation?.phoneNumber,
                                    email: activeConversation?.email,
                                },
                                '-',
                            )}
                        </Typography>
                        <Typography
                            variant="caption"
                            sx={{ cursor: 'pointer', color: '#1F4C5C' }}
                            onClick={onClickContactDetails}
                        >
                            {t('InboxPage.viewDetails')}
                        </Typography>
                    </div>
                    {activeConversation?.assignedAgentId ? (
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                            <StyledAvatar size="small" icon={<PulseAiIcon />} />
                            <Button variant="outlined" disabled={!activeConversation} onClick={() => onAssignUser()}>
                                {t(
                                    isMobile || isLargeScreenWithOpenedContact
                                        ? 'buttons.join'
                                        : 'buttons.joinConversation',
                                )}
                            </Button>
                        </div>
                    ) : (
                        <div style={{ display: 'flex' }}>
                            <UsersMenu
                                options={usersOptions}
                                value={activeConversation.assignedUserId}
                                onSelect={onAssignUser}
                            />
                            <Button
                                variant="outlined"
                                disabled={!activeConversation || (isArchived && !isOpen) || isSolvingConversation}
                                onClick={onResolveConversation}
                            >
                                {t(isOpen ? 'buttons.resolve' : 'buttons.reopen')}
                            </Button>
                        </div>
                    )}
                </>
            )}
        </>
    );
};

export default InboxHeader;
